import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../../common/AppBar.js";
import Snackbar from "../../../common/Snackbar.js";
import RenderHtml from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import {
  HeadingWithTreePath,
  generateCompleteTreePath,
} from "../../SubassemblyDetails/utils/treePathUtils.js";

/**
 * SIBLING ORDER LOGIC FOR MOVING CHILD NODES:
 *
 * When moving a child node (video, rich text, canvas, etc.) above or below another node:
 *
 * 1. SAME PARENT (same step/substep):
 *    - Move ABOVE: to_order = selected element's sibling order - 1
 *    - Move BELOW: to_order = selected element's sibling order (same as target)
 *
 * 2. DIFFERENT PARENT (different step/substep):
 *    - Move ABOVE: to_order = selected element's sibling order (same as target)
 *    - Move BELOW: to_order = selected element's sibling order + 1
 *
 * Examples:
 * - Moving Video 4 below Rich Text 3 (same step): to_order = 3 (same as target)
 * - Moving Video 4 above Video 5 (same step): to_order = 4 (5-1)
 * - Moving Video 4 above Canvas 3 (different step): to_order = 3 (same as target)
 * - Moving Video 4 below Canvas 3 (different step): to_order = 4 (3+1)
 */

import "../../SubassemblyDetails/SubassemblyDetails.css";
import { getPages, renderPreview, renderVideo } from "../plugins/utils.js";
import { getAllSectionsForPart } from "../../../redux/sectionSlice.js";
import {
  getAllAttachmentsForStep,
  getAllAttachmentsForSubstep,
  getAllCanvasesForStep,
  getAllCanvasesForSubstep,
  getAllFormsForStep,
  getAllFormsForSubstep,
  getAllRichTextsForStep,
  getAllRichTextsForSubstep,
  getAllStepsForSection,
  getAllSubstepsForStep,
  getAllVideosForStep,
  getAllVideosForSubstep,
} from "../../../redux/stepSlice.js";

import CanvasDisplay from "../../Canvas/components/canvas-core/CanvasDisplay.jsx";
import { getMoveCopyInfo } from "../../../util/AppUtils.js";
import { BASE } from "../../../config";
const ProcedureVideoMove = () => {
  const [targetSectionData, setTargetSectionData] = useState({});
  const { sections } = useSelector((state) => state.section);
  let richTextCounter = 1;
  let canvasCounter = 1;
  let videoCounter = 1;
  let formCounter = 1;
  const [createCopy, setCreateCopy] = useState(false);

  let richTextsCounter = {};

  const location = useLocation();
  const navigate = useNavigate();
  let attachmentCounter = 1;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const {
    procedure_tree_ref: fromProcedureTreeRef,
    type,
    partNumber,
    description,
    revision,
    status,
    title,
    steps,
    revisionId,
    partId,
    fromSectionId,
    fromTreeId,
    fromSiblingOrder,
    fromStepSiblingOrder,
    fromStepId,
    fromStepTreeId,
    fromSubstepId,
    fromSubstepTreeId,
    fromSubstepSiblingOrder,
    elementId,
  } = location.state;

  // Extract source information from title and construct the title early
  const titleParts = title.split(" ");

  // Extract step number - prioritize location.state.stepNumber for accuracy, then parse from title
  const sourceStepNumber = location.state.stepNumber
    ? parseInt(location.state.stepNumber, 10)
    : parseInt(titleParts[4], 10); // Video title format: "Video X from Step Y"

  // Use location.state values if they're provided (more reliable), otherwise parse from title
  const sourceVideoNumber =
    location.state.videoNumber &&
    !isNaN(parseInt(location.state.videoNumber, 10))
      ? parseInt(location.state.videoNumber, 10)
      : parseInt(titleParts[1]); // Video title format: "Video X from Step Y"

  const stepNumberFromState =
    location.state.stepNumber && !isNaN(parseInt(location.state.stepNumber, 10))
      ? parseInt(location.state.stepNumber, 10)
      : sourceStepNumber;

  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [selectedStepInfo, setSelectedStepInfo] = useState(null);
  const [selectedNodeInfo, setSelectedNodeInfo] = useState(null);
  const [selectionType, setSelectionType] = useState(null); // 'section', 'step', or 'node'
  const [toMoveCopyInfo, setToMoveCopy] = useState({});

  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
  } = useSelector((state) => state.step);

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  // Debug function to log current state
  const logCurrentState = () => {
  };

  const sortedSections = [...sections];

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreateCopyChange = () => {
    setCreateCopy(!createCopy);
  };

  const handleMoveUp = () => {

    // Log current state for debugging
    logCurrentState();

    if (!selectedNodeInfo) {
      showSnackbar("Please select a target node to move above", "error");
      return;
    }

    // Validate that selectedNodeInfo has the required properties

    if (
      selectedNodeInfo.sectionIndex === undefined ||
      selectedNodeInfo.stepIndex === undefined ||
      !selectedNodeInfo.stepId
    ) {
      console.error("Selected node info validation failed:", selectedNodeInfo);
      showSnackbar(
        "Invalid target selection. Please select a valid node.",
        "error"
      );
      return;
    }

    // Additional validation: ensure the selected node is not a substep
    if (selectedNodeInfo.nodeType === "Substep") {
      showSnackbar(
        "Cannot move above/below a substep. Use 'Move Into' instead.",
        "error"
      );
      return;
    }

    // Update direction for AppBar title
    setSelectedNodeInfo({
      ...selectedNodeInfo,
      direction: "up",
    });

    // Use the already parsed values from the top of the component

    // Use the sourceNodeIndex from location state (same as attachment move)
    let finalSourceNodeIndex = location.state.sourceNodeIndex;

    // Debug logging to understand the positioning issue

    // SPECIAL DEBUG FOR CANVAS TARGETS
    if (selectedNodeInfo.nodeType === "Canvas") {
    }


    // Calculate the actual target node index in the combined list of all items - HANDLE MOVE UP
    const targetStepId = selectedNodeInfo.stepId;

    // Use the same logic as the UI to create the combined items list - HANDLE MOVE UP
    // This ensures the order matches exactly what the user sees
    const combinedItems = [
      ...(richTexts[targetStepId] || []).map((item) => ({
        ...item,
        type: "richText",
      })),
      ...(forms[targetStepId] || []).map((item) => ({
        ...item,
        type: "form",
      })),
      ...(attachments[targetStepId] || []).map((item) => ({
        ...item,
        type: "attachment",
      })),
      ...(canvases[targetStepId] || []).map((item) => ({
        ...item,
        type: "canvas",
      })),
      ...(videos[targetStepId] || []).map((item) => ({
        ...item,
        type: "video",
      })),
    ].filter((item) => !item.substep_id_ref && !item.sub_step_tree_node_id);

    // Apply the same sorting logic as the UI
    const targetStepItems = combinedItems.sort((a, b) => {
      // Get sibling orders, defaulting to empty string if not available
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order || "";
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order || "";

      // If both have sibling orders, compare them numerically
      if (orderA && orderB) {
        const orderCompare = compareSiblingOrders(orderA, orderB);
        if (orderCompare !== 0) return orderCompare;
      }

      // If only one has a sibling order, prioritize it
      if (orderA && !orderB) return -1;
      if (!orderA && orderB) return 1;

      // If no sibling orders, sort by created_at date if available
      const dateA = a.created_at ? new Date(a.created_at) : null;
      const dateB = b.created_at ? new Date(b.created_at) : null;

      if (dateA && dateB) return dateA - dateB; // Older items first

      // For consistent sorting when other methods fail, sort by type
      const typeOrder = {
        richText: 1,
        form: 2,
        attachment: 3,
        canvas: 4,
        video: 5,
      };
      return typeOrder[a.type] - typeOrder[b.type];
    });

    // Find the target item in the combined list - HANDLE MOVE UP
    let actualTargetNodeIndex = targetStepItems.length; // Default to end
    if (selectedNodeInfo.nodeId) {
      const targetItem = targetStepItems.find((item) => {
        const itemId =
          item.rich_text_node_id ||
          item.form_tree_node_id ||
          item.attachment_tree_node_id ||
          item.canvas_tree_node_id ||
          item.video_tree_node_id;
        return itemId === selectedNodeInfo.nodeId;
      });

      if (targetItem) {
        actualTargetNodeIndex = targetStepItems.indexOf(targetItem);

        // CRITICAL FIX: For "move up" operations, ensure we're targeting the correct item
        if (selectedNodeInfo.nodeType === "Rich Text") {

          const richTextItems = targetStepItems.filter(
            (item) => item.type === "richText"
          );
          richTextItems.forEach((item, idx) => {
          });

          // Verify we're targeting the correct Rich Text item
          const expectedRichText = richTextItems.find(
            (item) =>
              item.rich_text_node_id === selectedNodeInfo.nodeId ||
              (item.global_node_number || item.globalNodeNumber) ===
                selectedNodeInfo.globalNodeNumber
          );

          if (expectedRichText) {
            const expectedIndex = targetStepItems.indexOf(expectedRichText);
            if (expectedIndex !== actualTargetNodeIndex) {
              actualTargetNodeIndex = expectedIndex;
            }
          }
        }
      }
    }

    // Debug: Log all items in the combined list to see the order


    // CRITICAL FIX: Calculate the correct sibling order for "move above" operations
    // For "move above":
    // - Same step: to_order = selected element's sibling order - 1
    // - Different step: to_order = selected element's sibling order (same as target)
    let expectedSiblingOrder = 1; // Default value
    if (actualTargetNodeIndex < targetStepItems.length) {
      const targetItem = targetStepItems[actualTargetNodeIndex];
      const targetSiblingOrder =
        targetItem.sibling_order || targetItem.procedure_tree?.sibling_order;

      if (targetSiblingOrder) {
        const targetOrder = parseInt(targetSiblingOrder, 10);

        // Check if we're moving within the same step/substep (same parent)
        const isSameParent =
          location.state.sourceStepId === selectedNodeInfo.stepId;

        if (isSameParent) {
          // Moving within same parent: use target's sibling order - 1 to position BEFORE
          expectedSiblingOrder = Math.max(1, targetOrder - 1);
        } else {
          // Moving to different parent: use target's sibling order directly (same as target)
          expectedSiblingOrder = targetOrder;
        }
      }
    }


    // Get the source video's current sibling order
    const sourceVideo = videos[location.state.sourceStepId]?.find(
      (video) => video.video_tree_node_id === elementId
    );
    const sourceSiblingOrder =
      sourceVideo?.sibling_order ||
      sourceVideo?.procedure_tree?.sibling_order ||
      1;


    // Navigate to preview screen with all relevant data
    navigate("/procedure-video-move-preview", {
      state: {
        ...location.state,
        move_rev_node_info: {
          ...location.state.move_rev_node_info,
          ...toMoveCopyInfo,
          direction: "above",
        },
        fromProcedureTreeRef,
        moveType: "up",
        targetSectionIndex: selectedNodeInfo.sectionIndex,
        targetStepIndex: selectedNodeInfo.stepIndex,
        targetNodeIndex: actualTargetNodeIndex, // Use the calculated actual index
        targetNodeType: selectedNodeInfo.nodeType,
        targetNodeGlobalNumber: selectedNodeInfo.globalNodeNumber,
        targetNodeId: selectedNodeInfo.nodeId, // CRITICAL: Pass the target node ID for precise positioning
        expectedSiblingOrder: expectedSiblingOrder, // Pass the calculated sibling order
        sourceSiblingOrder: sourceSiblingOrder, // Pass the source node's current sibling order
        sourceStepIndex: location.state.sourceStepIndex,
        sourceSectionIndex: location.state.sourceSectionIndex,
        sourceNodeIndex: finalSourceNodeIndex,
        sourceNodeType: "video",
        movingNode: true,
        sourceStepId: location.state.sourceStepId,
        targetStepId: selectedNodeInfo.stepId,
        sourceSubstepIndex: location.state.sourceSubstepIndex,
        revisionId,
        targetSectionData,
        partId,
        fromSectionId,
        fromTreeId,
        fromSiblingOrder,
        fromStepSiblingOrder,
        fromStepId,
        fromStepTreeId,
        fromSubstepId,
        elementId,
        fromSubstepTreeId,
        fromSubstepSiblingOrder,
        createCopy, // Add createCopy state
        title: `Moving Video ${sourceVideoNumber} from Step ${sourceStepNumber}`,
        ...(selectedNodeInfo.substepIndex !== undefined && {
          targetSubstepIndex: selectedNodeInfo.substepIndex,
        }),
      },
    });
  };

  const handleMoveInto = () => {

    // Log current state for debugging
    logCurrentState();

    if (
      !selectedStepInfo &&
      (!selectedNodeInfo || selectedNodeInfo.nodeType !== "Substep")
    ) {
      showSnackbar("Please select a target node to move into", "error");
      return;
    }

    // Use the already parsed values from the top of the component

    // Use the sourceNodeIndex from location state (same as attachment move)
    let finalSourceNodeIndex = location.state.sourceNodeIndex;

    // Debug logging to understand the positioning issue

    // Determine if target is a step or substep
    const isTargetSubstep =
      selectedNodeInfo && selectedNodeInfo.nodeType === "Substep";

    // Safely access properties with null checks
    const targetSectionIndex = isTargetSubstep
      ? selectedNodeInfo?.sectionIndex
      : selectedStepInfo?.sectionIndex;

    const targetStepIndex = isTargetSubstep
      ? selectedNodeInfo?.stepIndex
      : selectedStepInfo?.stepIndex;

    const targetStepId = isTargetSubstep
      ? selectedNodeInfo?.stepId
      : selectedStepInfo?.stepId;

    // Validate that we have the required target information
    if (
      targetSectionIndex === undefined ||
      targetStepIndex === undefined ||
      targetStepId === undefined
    ) {
      showSnackbar(
        "Invalid target selection. Please select a valid step or substep.",
        "error"
      );
      return;
    }


    // Get the source video's current sibling order
    const sourceVideo = videos[location.state.sourceStepId]?.find(
      (video) => video.video_tree_node_id === elementId
    );
    const sourceSiblingOrder =
      sourceVideo?.sibling_order ||
      sourceVideo?.procedure_tree?.sibling_order ||
      1;


    navigate("/procedure-video-move-preview", {
      state: {
        ...location.state,
        move_rev_node_info: {
          ...location.state.move_rev_node_info,
          ...toMoveCopyInfo,
          direction: "into",
        },
        fromProcedureTreeRef,
        moveType: "into",
        targetSectionIndex,
        targetStepIndex,
        targetNodeId: selectedNodeInfo?.nodeId, // CRITICAL: Pass the target node ID for precise positioning
        sourceSiblingOrder: sourceSiblingOrder, // Pass the source node's current sibling order
        sourceStepIndex: location.state.sourceStepIndex,
        sourceSectionIndex: location.state.sourceSectionIndex,
        sourceNodeIndex: finalSourceNodeIndex,
        sourceNodeType: "video",
        movingNode: true,
        sourceStepId: location.state.sourceStepId,
        targetStepId,
        sourceSubstepIndex: location.state.sourceSubstepIndex,
        revisionId,
        targetSectionData,
        partId,
        fromSectionId,
        fromTreeId,
        fromSiblingOrder,
        fromStepSiblingOrder,
        fromStepId,
        fromStepTreeId,
        fromSubstepId,
        elementId,
        fromSubstepTreeId,
        fromSubstepSiblingOrder,
        createCopy, // Add createCopy state
        title: `Moving Video ${sourceVideoNumber} from Step ${sourceStepNumber}`,
        ...(isTargetSubstep && {
          targetSubstepIndex: selectedNodeInfo.substepIndex,
        }),
      },
    });
  };

  const handleMoveBelow = () => {

    // Log current state for debugging
    logCurrentState();

    if (!selectedNodeInfo) {
      showSnackbar("Please select a target node to move below", "error");
      return;
    }

    // Validate that selectedNodeInfo has the required properties

    if (
      selectedNodeInfo.sectionIndex === undefined ||
      selectedNodeInfo.stepIndex === undefined ||
      !selectedNodeInfo.stepId
    ) {
      console.error("Selected node info validation failed:", selectedNodeInfo);
      showSnackbar(
        "Invalid target selection. Please select a valid node.",
        "error"
      );
      return;
    }

    // Additional validation: ensure the selected node is not a substep
    if (selectedNodeInfo.nodeType === "Substep") {
      showSnackbar(
        "Cannot move above/below a substep. Use 'Move Into' instead.",
        "error"
      );
      return;
    }

    // Update direction for AppBar title
    setSelectedNodeInfo({
      ...selectedNodeInfo,
      direction: "below",
    });

    // Use the already parsed values from the top of the component

    // Use the sourceNodeIndex from location state (same as attachment move)
    let finalSourceNodeIndex = location.state.sourceNodeIndex;

    // Debug logging to understand the positioning issue

    // SPECIAL DEBUG FOR CANVAS TARGETS
    if (selectedNodeInfo.nodeType === "Canvas") {
    }


    // Calculate the actual target node index in the combined list of all items - HANDLE MOVE BELOW
    const targetStepId = selectedNodeInfo.stepId;

    // Use the same logic as the UI to create the combined items list - HANDLE MOVE BELOW
    // This ensures the order matches exactly what the user sees
    const combinedItems = [
      ...(richTexts[targetStepId] || []).map((item) => ({
        ...item,
        type: "richText",
      })),
      ...(forms[targetStepId] || []).map((item) => ({
        ...item,
        type: "form",
      })),
      ...(attachments[targetStepId] || []).map((item) => ({
        ...item,
        type: "attachment",
      })),
      ...(canvases[targetStepId] || []).map((item) => ({
        ...item,
        type: "canvas",
      })),
      ...(videos[targetStepId] || []).map((item) => ({
        ...item,
        type: "video",
      })),
    ].filter((item) => !item.substep_id_ref && !item.sub_step_tree_node_id);

    // Apply the same sorting logic as the UI
    const targetStepItems = combinedItems.sort((a, b) => {
      // Get sibling orders, defaulting to empty string if not available
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order || "";
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order || "";

      // If both have sibling orders, compare them numerically
      if (orderA && orderB) {
        const orderCompare = compareSiblingOrders(orderA, orderB);
        if (orderCompare !== 0) return orderCompare;
      }

      // If only one has a sibling order, prioritize it
      if (orderA && !orderB) return -1;
      if (!orderA && orderB) return 1;

      // If no sibling orders, sort by created_at date if available
      const dateA = a.created_at ? new Date(a.created_at) : null;
      const dateB = b.created_at ? new Date(b.created_at) : null;

      if (dateA && dateB) return dateA - dateB; // Older items first

      // For consistent sorting when other methods fail, sort by type
      const typeOrder = {
        richText: 1,
        form: 2,
        attachment: 3,
        canvas: 4,
        video: 5,
      };
      return typeOrder[a.type] - typeOrder[b.type];
    });

    // Sort items by sibling order to maintain correct sequence
    targetStepItems.sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order || "";
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order || "";

      if (orderA && orderB) {
        const intA = parseInt(orderA, 10);
        const intB = parseInt(orderB, 10);
        return intA - intB;
      }

      if (orderA && !orderB) return -1;
      if (!orderA && orderB) return 1;

      // Fallback to created_at date
      const dateA = a.created_at ? new Date(a.created_at) : null;
      const dateB = b.created_at ? new Date(b.created_at) : null;
      if (dateA && dateB) return dateA - dateB;

      // Instead of using type order, preserve the original order by using created_at
      // This ensures items appear in the order they were created, which is closer to visual order
      if (dateA && dateB) return dateA - dateB;

      // If no dates, preserve the order they appear in the original arrays
      // This is a better fallback than arbitrary type ordering
      return 0;
    });

    // Find the target item in the combined list
    let actualTargetNodeIndex = targetStepItems.length; // Default to end
    if (selectedNodeInfo.nodeId) {
      const targetItem = targetStepItems.find((item) => {
        const itemId =
          item.rich_text_node_id ||
          item.form_tree_node_id ||
          item.attachment_tree_node_id ||
          item.canvas_tree_node_id ||
          item.video_tree_node_id;
        return itemId === selectedNodeInfo.nodeId;
      });

      if (targetItem) {
        actualTargetNodeIndex = targetStepItems.indexOf(targetItem);

        // CRITICAL FIX: Log the target item details for debugging

        // CRITICAL FIX: For "move below" operations, ensure we're targeting the correct item
        if (selectedNodeInfo.nodeType === "Rich Text") {

          const richTextItems = targetStepItems.filter(
            (item) => item.type === "richText"
          );
          richTextItems.forEach((item, idx) => {
          });

          // Verify we're targeting the correct Rich Text item
          const expectedRichText = richTextItems.find(
            (item) =>
              item.rich_text_node_id === selectedNodeInfo.nodeId ||
              (item.global_node_number || item.globalNodeNumber) ===
                selectedNodeInfo.globalNodeNumber
          );

          if (expectedRichText) {
            const expectedIndex = targetStepItems.indexOf(expectedRichText);
            if (expectedIndex !== actualTargetNodeIndex) {
              actualTargetNodeIndex = expectedIndex;
            }
          }
        }
      }
    }


    // CRITICAL FIX: Calculate the correct sibling order for "move below" operations
    // For "move below":
    // - Same step: to_order = selected element's sibling order (same as target)
    // - Different step: to_order = selected element's sibling order + 1
    let expectedSiblingOrder = 1; // Default value
    if (actualTargetNodeIndex < targetStepItems.length) {
      const targetItem = targetStepItems[actualTargetNodeIndex];
      const targetSiblingOrder =
        targetItem.sibling_order || targetItem.procedure_tree?.sibling_order;

      if (targetSiblingOrder) {
        const targetOrder = parseInt(targetSiblingOrder, 10);

        // Check if we're moving within the same step/substep (same parent)
        const isSameParent =
          location.state.sourceStepId === selectedNodeInfo.stepId;

        if (isSameParent) {
          // Moving within same parent: use target's sibling order (same as target) to position AFTER
          expectedSiblingOrder = targetOrder;
        } else {
          // Moving to different parent: use target's sibling order + 1 to position AFTER
          expectedSiblingOrder = targetOrder + 1;
        }

        // Ensure the calculated order is valid
        if (expectedSiblingOrder <= 0) {
          expectedSiblingOrder = targetOrder + 1;
        }
      }
    }


    // Get the source video's current sibling order
    const sourceVideo = videos[location.state.sourceStepId]?.find(
      (video) => video.video_tree_node_id === elementId
    );
    const sourceSiblingOrder =
      sourceVideo?.sibling_order ||
      sourceVideo?.procedure_tree?.sibling_order ||
      1;


    // Navigate to preview screen
    navigate("/procedure-video-move-preview", {
      state: {
        ...location.state,
        move_rev_node_info: {
          ...location.state.move_rev_node_info,
          ...toMoveCopyInfo,
          direction: "below",
        },
        fromProcedureTreeRef,
        moveType: "below",
        targetSectionIndex: selectedNodeInfo.sectionIndex,
        targetStepIndex: selectedNodeInfo.stepIndex,
        targetNodeIndex: actualTargetNodeIndex, // Use the calculated actual index
        targetNodeType: selectedNodeInfo.nodeType,
        targetNodeGlobalNumber: selectedNodeInfo.globalNodeNumber,
        targetNodeId: selectedNodeInfo.nodeId, // CRITICAL: Pass the target node ID for precise positioning
        expectedSiblingOrder: expectedSiblingOrder, // Pass the calculated sibling order
        sourceSiblingOrder: sourceSiblingOrder, // Pass the source node's current sibling order
        sourceStepIndex: location.state.sourceStepIndex,
        sourceSectionIndex: location.state.sourceSectionIndex,
        sourceNodeIndex: finalSourceNodeIndex,
        sourceNodeType: "video",
        movingNode: true,
        sourceStepId: location.state.sourceStepId,
        targetStepId: selectedNodeInfo.stepId,
        sourceSubstepIndex: location.state.sourceSubstepIndex,
        revisionId,
        targetSectionData,
        partId,
        fromSectionId,
        fromTreeId,
        fromSiblingOrder,
        fromStepSiblingOrder,
        fromStepId,
        fromStepTreeId,
        fromSubstepId,
        fromSubstepTreeId,
        fromSubstepSiblingOrder,
        elementId,
        createCopy, // Add createCopy state
        title: `Moving Video ${sourceVideoNumber} from Step ${sourceStepNumber}`,
        ...(selectedNodeInfo.substepIndex !== undefined && {
          targetSubstepIndex: selectedNodeInfo.substepIndex,
        }),
      },
    });
  };

  // Get the buttons to display based on selection type
  const getButtons = () => {
    const buttons = [];


    if (
      selectionType === "step" ||
      (selectionType === "node" &&
        selectedNodeInfo &&
        selectedNodeInfo.nodeType === "Substep")
    ) {
      // Show Move Into/Copy Into button when step or substep is selected
      // Validate that we have the required information
      if (
        (selectionType === "step" && selectedStepInfo?.stepId) ||
        (selectionType === "node" &&
          selectedNodeInfo?.stepId &&
          selectedNodeInfo?.sectionIndex !== undefined &&
          selectedNodeInfo?.stepIndex !== undefined)
      ) {
        buttons.push({
          label: createCopy ? "Paste Into" : "Move Into",
          onClick: handleMoveInto,
        });
      } else {
      }
    } else if (selectionType === "node" && selectedNodeInfo) {
      // Only show Move Up/Copy Up and Move Below/Copy Below buttons when node is selected (but not a substep)
      // Validate that we have the required information
      if (
        selectedNodeInfo.stepId &&
        selectedNodeInfo.sectionIndex !== undefined &&
        selectedNodeInfo.stepIndex !== undefined
      ) {
        buttons.push({
          label: createCopy ? "Paste Up" : "Move Up",
          onClick: handleMoveUp,
        });
        buttons.push({
          label: createCopy ? "Paste Below" : "Move Below",
          onClick: handleMoveBelow,
        });
      } else {
      }
    }

    return buttons;
  };

  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      // setShowLoader(true);

      await dispatch(getAllSectionsForPart({ partId, revisionId }));
    })();
  }, [dispatch, partId, revisionId]);

  useEffect(() => {
    if (sections.length > 0) {
      sections.forEach(async (section, index) => {
        await dispatch(
          getAllStepsForSection({
            partId,
            revisionId,
            sectionId: section.section_tree_node_id,
          })
        );
      });
    }
  }, [sections, dispatch, partId, revisionId]);

  useEffect(() => {
    if (Object.keys(stepsBySection).length > 0) {
      Object.entries(stepsBySection).forEach(([sectionId, steps], index) => {
        steps.forEach(async (step) => {
          const { step_tree_node_id: stepId } = step;

          // // Fetch for step-level content
          await dispatch(
            getAllRichTextsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllFormsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllVideosForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllCanvasesForStep({ partId, revisionId, sectionId, stepId })
          );
          // Fetch substeps (async)
          await dispatch(
            getAllSubstepsForStep({ partId, revisionId, sectionId, stepId })
          ).then((action) => {
            const substeps = action.payload?.substeps || [];
            substeps.forEach((substep) => {
              const substepId = substep.sub_step_tree_node_id;

              dispatch(
                getAllRichTextsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllFormsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllVideosForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllAttachmentsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllCanvasesForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
            });
          });
          if (index === Object.entries(stepsBySection).length - 1) {
            // setShowLoader(false);
          }
        });
      });
    } else {
      // setShowLoader(false);
    }
  }, [sections, dispatch, partId, revisionId, stepsBySection]);

  return (
    <div>
      <AppBar
        showTablesDropdown={false}
        showBackButton={false}
        showCancelButton={true}
        showCreateCopyCheckbox={true}
        createCopy={createCopy}
        onCreateCopyChange={handleCreateCopyChange}
        title={`${createCopy ? "Copying" : "Moving"} ${title} ${
          selectionType === "step" && selectedStepInfo?.globalStepNumber
            ? `into Step ${selectedStepInfo.globalStepNumber}`
            : selectionType === "node" &&
              selectedNodeInfo?.nodeType === "Substep" &&
              selectedNodeInfo?.globalNodeNumber
            ? `into Substep ${selectedNodeInfo.globalNodeNumber}`
            : selectionType === "node" &&
              selectedNodeInfo?.nodeType &&
              selectedNodeInfo?.globalNodeNumber
            ? `${selectedNodeInfo.direction === "up" ? "above" : "to"} ${
                selectedNodeInfo.nodeType
              } ${selectedNodeInfo.globalNodeNumber}`
            : ""
        }`}
        buttons={getButtons()}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 0",
          flexWrap: "wrap", // for responsiveness
        }}
      >
        {/* Checkbox + Title + Description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              margin: 0,
            }}
          >
            {/* Hide procedure checkbox */}
            <span style={{ fontSize: "20px", fontWeight: 600 }}>
              {type} {partNumber}
            </span>
          </label>

          {/* Description */}
          {/* <span style={{ marginLeft: "2px", marginTop: "5px", color: "#666" }}>
            Description: {description}
          </span> */}
        </div>

        {/* Right-aligned section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginLeft: "auto",
          }}
        >
          <div>
            <strong>Status: </strong>
            <span style={{ color: "orange" }}>Not Submitted</span>
          </div>

          <div>
            <strong>Revision: </strong>
            <span>{revision}</span>
          </div>
        </div>
      </div>

      {sortedSections
        .sort((a, b) => {
          const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
          const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

          const orderCompare = compareSiblingOrders(orderA, orderB);
          if (orderCompare !== 0) return orderCompare;

          // Fallback: compare created_at
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA; // latest comes first
        })
        .map((section, index) => {
          const selectedSection =
            index + 1 === selectedSectionIndex ||
            steps[0].procedure_tree.section_id_ref ==
              section.section_tree_node_id;

          const isDisabled = selectedSection || index === selectedSectionIndex;
          const sectionHeading = `Section ${index + 1}`;
          return (
            <div
              key={section.section_tree_node_id}
              style={{
                margin: "20px 0",
                clear: "both",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  clear: "both",
                }}
              >
                {/* Hide section checkbox */}
                {/* Simple heading for sections without tree path */}
                <h2 style={{ fontSize: "20px", margin: 0, clear: "both" }}>
                  {sectionHeading}
                </h2>
              </label>
              <hr />
              <hr />
              <div style={{ clear: "both" }}>{section.section_name}</div>
              <div style={{ clear: "both" }}>{section.short_description}</div>

              {/* Steps for this section */}
              {(stepsBySection[section.section_tree_node_id] || []).map(
                (step, stepIndex) => {
                  const stepId = step.step_tree_node_id;

                  // Calculate current step details for step identification
                  const stepsBeforeThisSection = sortedSections
                    .slice(0, index)
                    .reduce(
                      (acc, s) =>
                        acc +
                        (stepsBySection[s.section_tree_node_id]?.length || 0),
                      0
                    );
                  const globalStepNumber =
                    stepsBeforeThisSection + stepIndex + 1;

                  // Check if this is the source step that contains the element being moved
                  const isSourceStep = globalStepNumber === stepNumberFromState;

                  // Debug logging for source step items
                  if (isSourceStep) {

                    // Log source step videos for debugging
                    const stepVideos = videos[stepId] || [];
                    stepVideos.forEach((video, idx) => {
                    });
                  }

                  const myVideos = videos[stepId] || [];
                  const myForms = forms[stepId] || [];
                  const myCanvases = canvases[stepId] || [];
                  const myRichTexts = canvases[stepId] || [];

                  const currentRichTextCounter = richTextCounter;

                  // DO NOT increment the counters here - they should start at 1 for each step
                  // and increment as we render each item

                  richTextsCounter[stepId] = currentRichTextCounter;

                  const combinedItems = [
                    ...(richTexts[stepId] || []).map((item) => ({
                      ...item,
                      type: "richText",
                    })),
                    ...(forms[stepId] || []).map((item) => ({
                      ...item,
                      type: "form",
                    })),
                    ...(attachments[stepId] || []).map((item) => ({
                      ...item,
                      type: "attachment",
                    })),
                    ...(canvases[stepId] || []).map((item) => ({
                      ...item,
                      type: "canvas",
                    })),
                    ...(videos[stepId] || []).map((item) => ({
                      ...item,
                      type: "video",
                    })),
                  ]; // Filter out substeps or unwanted items (same as your current filter)
                  const filteredItems = combinedItems.filter(
                    (item) =>
                      !item.substep_id_ref && !item.sub_step_tree_node_id
                  );

                  filteredItems.sort((a, b) => {
                    // Get sibling orders, defaulting to empty string if not available
                    const orderA =
                      a.sibling_order || a.procedure_tree?.sibling_order || "";
                    const orderB =
                      b.sibling_order || b.procedure_tree?.sibling_order || "";

                    // If both have sibling orders, compare them numerically
                    if (orderA && orderB) {
                      const orderCompare = compareSiblingOrders(orderA, orderB);
                      if (orderCompare !== 0) return orderCompare;
                    }

                    // If only one has a sibling order, prioritize it
                    if (orderA && !orderB) return -1;
                    if (!orderA && orderB) return 1;

                    // If no sibling orders, sort by created_at date if available
                    const dateA = a.created_at ? new Date(a.created_at) : null;
                    const dateB = b.created_at ? new Date(b.created_at) : null;

                    if (dateA && dateB) return dateA - dateB; // Older items first

                    // For consistent sorting when other methods fail, sort by type
                    const typeOrder = {
                      richText: 1,
                      form: 2,
                      attachment: 3,
                      canvas: 4,
                      video: 5,
                    };
                    return typeOrder[a.type] - typeOrder[b.type];
                  });

                  const stepHeading = `Step ${globalStepNumber}`;
                  return (
                    <div
                      key={stepId}
                      style={{ marginTop: "20px", clear: "both" }}
                    >
                      <label
                        style={{
                          display: "flex",
                          gap: "8px",
                          cursor: "pointer",
                          clear: "both",
                        }}
                      >
                        <input
                          type="checkbox"
                          onChange={() => {
                            if (
                              selectedStepInfo &&
                              selectedStepInfo.stepId === stepId
                            ) {
                              setSelectedStepInfo(null);
                              setSelectionType(null);
                            } else {
                              setSelectedStepInfo({
                                stepId,
                                sectionIndex: index,
                                stepIndex: stepIndex,
                                globalStepNumber,
                              });
                              // Clear any substep selection when step is selected
                              setSelectedNodeInfo(null);
                              setTargetSectionData({
                                sectionId: section.section_tree_node_id,
                                siblingOrder: section.sibling_order,
                                sectionTreeId: section.section_tree_node_id,
                                stepId,
                                stepTreeId: step.procedure_tree_ref,
                                stepSiblingOrder:
                                  step.sibling_order ||
                                  step.procedure_tree?.sibling_order,
                              });
                              setSelectionType("step");
                            }
                            setToMoveCopy(
                              getMoveCopyInfo(
                                "to",
                                sectionHeading,
                                stepHeading,
                                null,
                                null
                              )
                            );
                          }}
                          checked={
                            selectedStepInfo &&
                            selectedStepInfo.stepIndex === stepIndex &&
                            selectedStepInfo.sectionIndex === index
                            // Remove the condition that checks if a node in this step is selected
                          }
                        />

                        <HeadingWithTreePath
                          title={stepHeading}
                          fontSize="20px" // Use consistent font size with child nodes
                          treePathParams={{
                            sectionIndex: index,
                            stepIndex: globalStepNumber - 1,
                            substepIndex: undefined,
                            nodeType: undefined,
                            nodeIndex: undefined,
                          }}
                          color="#0d6efd" // Blue color for all steps
                        />
                      </label>
                      <hr />
                      <p style={{ whiteSpace: 'pre-wrap' }}>{step.short_description || ""}</p>
                      <p>
                        <p>
                          {structuredClone(step.step_details)
                            .sort(
                              (a, b) =>
                                new Date(a.created_at) - new Date(b.created_at)
                            )
                            .map((myStep) => {
                              const partNumber =
                                myStep.part_info?.part_number || "";
                              const description =
                                myStep.part_info?.description || "";
                              const qtyText =
                                myStep.qty && myStep.qty != 0
                                  ? `Qty ${myStep.qty}`
                                  : "";

                              const partText = partNumber
                                ? ` (${partNumber})`
                                : "";

                              return (
                                <>
                                  {myStep.action}
                                  {partText}{" "}
                                  <RenderHtml content={description} /> {qtyText}
                                  &nbsp;
                                </>
                              );
                            })}
                        </p>
                      </p>

                      <h4>{step.bom_table_title_info?.category_name || ""}</h4>

                      <table className="table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>
                              {
                                step.part_number_column_title_ref_info
                                  ?.category_name
                              }{" "}
                            </th>
                            <th>Description</th>
                            <th>Qty Per</th>
                            <th>Unit</th>
                          </tr>
                        </thead>

                        <tbody>
                          {step.parts_required.map((row) => (
                            <tr>
                              <td>{row.part_info.type_info.name}</td>
                              <td>{row.part_info.part_number}</td>
                              <td>
                                <RenderHtml
                                  content={row.part_info.description}
                                />{" "}
                              </td>
                              <td>{row.qty === 0 ? "" : row.qty}</td>
                              <td>{row.part_info?.unit_info?.unit_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Step Content */}
                      {filteredItems.map((item, itemIndex) => {
                        if (item.substep_id_ref || item.sub_step_tree_node_id)
                          return null;

                        // Pre-calculate node numbers for comparison before incrementing counters
                        const currentAttachmentNumber = attachmentCounter;
                        const currentRichTextNumber = richTextCounter;
                        const currentCanvasNumber = canvasCounter;
                        const currentFormNumber = formCounter;

                        // Debug log counters when in source step
                        if (isSourceStep) {
                        }

                        // Determine if this is the source video being moved
                        const isSourceItem =
                          isSourceStep &&
                          item.type === "video" &&
                          item.video_tree_node_id === elementId;

                        // Debug log if this is the source item
                        if (isSourceItem) {
                        }

                        switch (item.type) {
                          case "richText": {
                            const globalRichTextNumber = richTextCounter++; // ✅ use then increment
                            const nodeHeading = `Rich Text ${globalRichTextNumber}`;
                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "richText",
                                  nodeIndex: globalRichTextNumber - 1,
                                }}
                                color="#0d6efd" // Blue color for all items
                              />
                            );

                            return (
                              <div
                                key={item.rich_text_node_id}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.rich_text_node_id
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        // When selecting a node, clear any step selection
                                        setSelectedStepInfo(null);
                                        const nodeInfo = {
                                          nodeId: item.rich_text_node_id,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex,
                                          globalNodeNumber:
                                            globalRichTextNumber,
                                          nodeType: "Rich Text",
                                          direction: null,
                                          stepId,
                                        };
                                        setSelectedNodeInfo(nodeInfo);
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId: item.rich_text_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      selectedNodeInfo &&
                                      selectedNodeInfo.nodeId ===
                                        item.rich_text_node_id
                                    }
                                    style={
                                      isSourceItem
                                        ? {
                                            accentColor: "#444444", // Dark grey for element being moved
                                            backgroundColor: "#444444",
                                          }
                                        : selectedNodeInfo &&
                                          selectedNodeInfo.nodeId ===
                                            item.rich_text_node_id
                                        ? {
                                            accentColor: "#0066cc", // Blue for selected node
                                            backgroundColor: "#0066cc",
                                          }
                                        : {}
                                    }
                                    disabled={isSourceItem}
                                  />
                                  {label}
                                </label>

                                <div
                                  style={{
                                    marginLeft: "5px",
                                    marginTop: "-10px",
                                    clear: "both",
                                  }}
                                >
                                  {item.short_description}
                                </div>
                                <div
                                  style={{
                                    marginLeft: "4px",
                                    marginTop: "5px",
                                    clear: "both",
                                  }}
                                >
                                  {item.category_data?.category_name}
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    marginLeft: "4px",
                                    marginTop: "5px",
                                    clear: "both",
                                  }}
                                >
                                  <div
                                    style={{ marginTop: "-6px", clear: "both" }}
                                    dangerouslySetInnerHTML={{
                                      __html: item.rich_text_content,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          }
                          case "video": {
                            // Get the display video number for this step
                            const displayVideoNumber = videoCounter++;

                            // Debug video numbering
                            if (isSourceStep) {
                            }

                            // Determine if this is the source video using the same logic as other items
                            const isMovingThisVideo = isSourceItem;

                            // Debug log for video component
                            if (isSourceStep) {
                            }

                            const nodeHeading = `Video ${displayVideoNumber}`;
                            // Use global counter for display
                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "video",
                                  nodeIndex: displayVideoNumber - 1,
                                }}
                                color={
                                  isMovingThisVideo ? undefined : "#0d6efd"
                                } // Blue color only for items NOT being moved
                              />
                            );

                            // Now render the component
                            return (
                              <div
                                key={item.video_tree_node_id || label}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.video_tree_node_id
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        // When selecting a node, clear any step selection
                                        setSelectedStepInfo(null);
                                        const nodeInfo = {
                                          nodeId: item.video_tree_node_id,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex,
                                          globalNodeNumber: displayVideoNumber,
                                          nodeType: "Video",
                                          direction: null,
                                          stepId,
                                        };
                                        setSelectedNodeInfo(nodeInfo);
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId: item.video_tree_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      isMovingThisVideo ||
                                      (selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.video_tree_node_id)
                                    }
                                    style={
                                      isMovingThisVideo
                                        ? {
                                            accentColor: "#444444", // Dark grey for video being moved
                                            backgroundColor: "#444444",
                                          }
                                        : {}
                                    }
                                    disabled={isMovingThisVideo}
                                  />
                                  {label}
                                </label>

                                {renderVideo(item)}

                                <div style={{ clear: "both" }}>
                                  {item.video_url}
                                </div>

                                <div style={{ clear: "both" }}>
                                  {item.short_description}
                                </div>
                              </div>
                            );
                          }

                          case "form": {
                            const globalFormNumber = formCounter++; // ✅ use then increment
                            const nodeHeading = `Form ${globalFormNumber}`;
                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "form",
                                  nodeIndex: globalFormNumber - 1,
                                }}
                                color="#0d6efd" // Blue color for all items
                              />
                            );

                            return (
                              <div
                                key={item.form_tree_node_id || label}
                                style={{ clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    marginTop: "20px",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.form_tree_node_id
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        // When selecting a node, clear any step selection
                                        setSelectedStepInfo(null);
                                        const nodeInfo = {
                                          nodeId: item.form_tree_node_id,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex,
                                          globalNodeNumber: globalFormNumber,
                                          nodeType: "Form",
                                          direction: null,
                                          stepId,
                                        };
                                        setSelectedNodeInfo(nodeInfo);
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId: item.form_tree_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      selectedNodeInfo &&
                                      selectedNodeInfo.nodeId ===
                                        item.form_tree_node_id
                                    }
                                    style={
                                      isSourceItem
                                        ? {
                                            accentColor: "#444444", // Dark grey for element being moved
                                            backgroundColor: "#444444",
                                          }
                                        : {}
                                    }
                                    disabled={isSourceItem}
                                  />
                                  {label}
                                </label>
                                <div style={{ clear: "both" }}>
                                  {item.short_description}
                                </div>
                              </div>
                            );
                          }
                          case "canvas": {
                            const pages = getPages(item);

                            const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                            const nodeHeading = `Canvas ${globalCanvasNumber}`;
                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "canvas",
                                  nodeIndex: globalCanvasNumber - 1,
                                }}
                                color="#0d6efd" // Blue color for all items
                              />
                            );

                            return (
                              <div
                                key={item.canvas_tree_node_id || label}
                                style={{ marginTop: "40px", clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.canvas_tree_node_id
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        setSelectedNodeInfo({
                                          nodeId: item.canvas_tree_node_id,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex, // Use the actual array index for positioning
                                          globalNodeNumber: globalCanvasNumber,
                                          nodeType: "Canvas",
                                          direction: null,
                                          stepId,
                                        });
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId: item.canvas_tree_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      selectedNodeInfo &&
                                      selectedNodeInfo.nodeId ===
                                        item.canvas_tree_node_id
                                    }
                                    style={
                                      isSourceItem
                                        ? {
                                            accentColor: "#444444", // Dark grey for element being moved
                                            backgroundColor: "#444444",
                                          }
                                        : {}
                                    }
                                    disabled={isSourceItem}
                                  />
                                  {label}
                                </label>

                                {/* Display all pages vertically */}
                                <CanvasDisplay pages={pages} />
                              </div>
                            );
                          }
                          case "attachment": {
                            const attachmentList = item.attachments || [];
                            if (attachmentList.length === 0) return null;

                            const globalAttachmentNumber = attachmentCounter++;
                            const nodeHeading = `Attachment ${globalAttachmentNumber}`;

                            // Debug log attachment number in source step
                            if (isSourceStep) {
                            }

                            const label = (
                              <HeadingWithTreePath
                                title={nodeHeading}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "attachment",
                                  nodeIndex: globalAttachmentNumber - 1,
                                }}
                                color="#0d6efd" // Blue color for all items
                              />
                            );

                            // Determine if this is the source attachment using a direct comparison
                            // This is a more straightforward way to identify the attachment being moved

                            return (
                              <div
                                key={
                                  item.attachment_tree_node_id ||
                                  `attachment-${itemIndex}`
                                }
                                style={{ clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    marginTop: "20px",
                                    clear: "both",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId ===
                                          item.attachment_tree_node_id
                                      ) {
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                      } else {
                                        // When selecting a node, clear any step selection
                                        setSelectedStepInfo(null);
                                        const nodeInfo = {
                                          nodeId: item.attachment_tree_node_id,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          nodeIndex: itemIndex,
                                          globalNodeNumber:
                                            globalAttachmentNumber,
                                          nodeType: "Attachment",
                                          direction: null,
                                          stepId,
                                        };
                                        setSelectedNodeInfo(nodeInfo);
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          elementSiblingOrder:
                                            item.sibling_order,
                                          elementId:
                                            item.attachment_tree_node_id,
                                          elementTreeId:
                                            item.procedure_tree_ref,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          null,
                                          nodeHeading
                                        )
                                      );
                                    }}
                                    checked={
                                      selectedNodeInfo &&
                                      selectedNodeInfo.nodeId ===
                                        item.attachment_tree_node_id
                                    }
                                    style={
                                      isSourceItem
                                        ? {
                                            accentColor: "#444444", // Dark grey for element being moved
                                            backgroundColor: "#444444",
                                          }
                                        : {}
                                    }
                                    disabled={isSourceItem}
                                  />
                                  {label}
                                </label>
                                <div style={{ clear: "both" }}>
                                  {item.short_description}
                                </div>
                                {attachmentList.map((attachment, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      marginLeft: "20px",
                                      clear: "both",
                                    }}
                                  >
                                    {renderPreview(attachment)}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          default:
                            return null;
                        }
                      })}

                      {/* Substeps */}
                      {(substepsByStep[stepId] || []).map(
                        (substep, substepIndex) => {
                          const substepId = substep.sub_step_tree_node_id;
                          const combinedItems = [
                            ...(richTexts[substepId] || []).map((item) => ({
                              ...item,
                              type: "richText",
                            })),
                            ...(forms[substepId] || []).map((item) => ({
                              ...item,
                              type: "form",
                            })),
                            ...(attachments[substepId] || []).map((item) => ({
                              ...item,
                              type: "attachment",
                            })),
                            ...(canvases[substepId] || []).map((item) => ({
                              ...item,
                              type: "canvas",
                            })),
                            ...(videos[substepId] || []).map((item) => ({
                              ...item,
                              type: "video",
                            })),
                          ]; // Filter out substeps or unwanted items (same as your current filter)
                          const filteredItems = combinedItems;

                          filteredItems.sort((a, b) => {
                            // Get sibling orders, defaulting to empty string if not available
                            const orderA =
                              a.sibling_order ||
                              a.procedure_tree?.sibling_order ||
                              "";
                            const orderB =
                              b.sibling_order ||
                              b.procedure_tree?.sibling_order ||
                              "";

                            // If both have sibling orders, compare them numerically
                            if (orderA && orderB) {
                              const orderCompare = compareSiblingOrders(
                                orderA,
                                orderB
                              );
                              if (orderCompare !== 0) return orderCompare;
                            }

                            // If only one has a sibling order, prioritize it
                            if (orderA && !orderB) return -1;
                            if (!orderA && orderB) return 1;

                            // If no sibling orders, sort by created_at date if available
                            const dateA = a.created_at
                              ? new Date(a.created_at)
                              : null;
                            const dateB = b.created_at
                              ? new Date(b.created_at)
                              : null;

                            if (dateA && dateB) return dateA - dateB; // Older items first

                            // For consistent sorting when other methods fail, sort by type
                            const typeOrder = {
                              richText: 1,
                              form: 2,
                              attachment: 3,
                              canvas: 4,
                              video: 5,
                            };
                            return typeOrder[a.type] - typeOrder[b.type];
                          });

                          // Global step number (same logic you already use for steps)
                          const stepsBeforeThisOne =
                            sortedSections
                              .slice(0, index)
                              .reduce((sectionAcc, s) => {
                                const steps =
                                  stepsBySection[s.section_tree_node_id] || [];
                                return sectionAcc + steps.length;
                              }, 0) + stepIndex;

                          const globalStepNumber = stepsBeforeThisOne + 1;

                          // Final substep label like Substep 5A
                          const substepLetter = String.fromCharCode(
                            65 + substepIndex
                          ); // A, B, C, ...

                          const globalSubstepLabel = `Substep ${globalStepNumber}${substepLetter}`;

                          return (
                            <div key={substepId} style={{ marginTop: "20px" }}>
                              <div>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    onChange={() => {
                                      if (
                                        selectedNodeInfo &&
                                        selectedNodeInfo.nodeId === substepId
                                      ) {
                                        // Deselect if already selected
                                        setSelectedNodeInfo(null);
                                        setSelectionType(null);
                                        setTargetSectionData({});
                                      } else {
                                        setSelectedNodeInfo({
                                          nodeId: substepId,
                                          sectionIndex: index,
                                          stepIndex: stepIndex,
                                          substepIndex: substepIndex,
                                          globalNodeNumber: `${globalStepNumber}${substepLetter}`,
                                          nodeType: "Substep",
                                          direction: null,
                                          stepId,
                                        });
                                        // Clear step selection when substep is selected
                                        setSelectedStepInfo(null);
                                        setTargetSectionData({
                                          sectionId:
                                            section.section_tree_node_id,
                                          siblingOrder: section.sibling_order,
                                          sectionTreeId:
                                            section.section_tree_node_id,
                                          stepId,
                                          stepTreeId: step.procedure_tree_ref,
                                          stepSiblingOrder:
                                            step.sibling_order ||
                                            step.procedure_tree?.sibling_order,
                                          substepId,
                                          substepTreeId:
                                            substep.procedure_tree_ref,
                                          substepSiblingOrder:
                                            substep.sibling_order ||
                                            substep.procedure_tree
                                              ?.sibling_order,
                                        });
                                        setSelectionType("node");
                                      }
                                      setToMoveCopy(
                                        getMoveCopyInfo(
                                          "to",
                                          sectionHeading,
                                          stepHeading,
                                          globalSubstepLabel,
                                          null
                                        )
                                      );
                                    }}
                                    checked={
                                      selectedNodeInfo &&
                                      selectedNodeInfo.nodeType === "Substep" &&
                                      selectedNodeInfo.nodeId === substepId
                                    }
                                    disabled={false}
                                    // disabled={false
                                    //   // (selectedNodeInfo &&
                                    //   //   selectedNodeInfo.nodeType ===
                                    //   //     "Substep" &&
                                    //   //   selectedNodeInfo.substepIndex !==
                                    //   //     substepIndex) ||
                                    //   // isSourceStep
                                    // }
                                  />
                                  <HeadingWithTreePath
                                    title={globalSubstepLabel}
                                    fontSize="20px" // Use consistent font size with child nodes
                                    treePathParams={{
                                      sectionIndex: index,
                                      stepIndex: globalStepNumber - 1,
                                      substepIndex: substepIndex,
                                      nodeType: undefined,
                                      nodeIndex: undefined,
                                    }}
                                    color="#0d6efd" // Blue color for all substeps
                                  />
                                </label>

                                <div style={{ whiteSpace: 'pre-wrap' }}>{substep.short_description}</div>
                              </div>

                              {/* Substep content */}
                              {filteredItems.map((item, itemIndex) => {
                                // Pre-calculate node numbers for comparison before incrementing counters
                                const currentAttachmentNumber =
                                  attachmentCounter;
                                const currentRichTextNumber = richTextCounter;
                                const currentCanvasNumber = canvasCounter;
                                const currentFormNumber = formCounter;

                                // Debug log counters when in source step
                                if (isSourceStep) {
                                }

                                // Determine if this is the source video being moved
                                const isSourceItem =
                                  isSourceStep &&
                                  item.type === "video" &&
                                  item.video_tree_node_id === elementId;

                                // Debug log if this is the source item
                                if (isSourceItem) {
                                }

                                switch (item.type) {
                                  case "richText": {
                                    const globalRichTextNumber =
                                      richTextCounter++; // ✅ use then increment
                                    const nodeHeading = `Rich Text ${globalRichTextNumber}`;
                                    const label = (
                                      <HeadingWithTreePath
                                        title={nodeHeading}
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "richText",
                                          nodeIndex: globalRichTextNumber - 1,
                                        }}
                                        color="#0d6efd" // Blue color for all items
                                      />
                                    );

                                    return (
                                      <div
                                        key={item.rich_text_node_id}
                                        style={{
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            clear: "both",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onChange={() => {
                                              if (
                                                selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.rich_text_node_id
                                              ) {
                                                setSelectedNodeInfo(null);
                                                setSelectionType(null);
                                              } else {
                                                // When selecting a node, clear any step or substep selection
                                                setSelectedStepInfo(null);
                                                setSelectedNodeInfo({
                                                  nodeId:
                                                    item.rich_text_node_id,
                                                  sectionIndex: index,
                                                  stepIndex: stepIndex,
                                                  nodeIndex: itemIndex, // Use the actual array index for positioning
                                                  globalNodeNumber:
                                                    globalRichTextNumber,
                                                  nodeType: "Rich Text",
                                                  direction: null,
                                                  substepIndex,
                                                  stepId,
                                                });
                                                setTargetSectionData({
                                                  sectionId:
                                                    section.section_tree_node_id,
                                                  siblingOrder:
                                                    section.sibling_order,
                                                  sectionTreeId:
                                                    section.section_tree_node_id,
                                                  stepId,
                                                  stepTreeId:
                                                    step.procedure_tree_ref,
                                                  stepSiblingOrder:
                                                    step.sibling_order ||
                                                    step.procedure_tree
                                                      ?.sibling_order,
                                                  elementSiblingOrder:
                                                    item.sibling_order,
                                                  elementId:
                                                    item.rich_text_node_id,
                                                  elementTreeId:
                                                    item.procedure_tree_ref,
                                                  substepId,
                                                  substepTreeId:
                                                    substep.procedure_tree_ref,
                                                  substepSiblingOrder:
                                                    substep.sibling_order ||
                                                    substep.procedure_tree
                                                      ?.sibling_order,
                                                });
                                                setSelectionType("node");
                                              }
                                              setToMoveCopy(
                                                getMoveCopyInfo(
                                                  "to",
                                                  sectionHeading,
                                                  stepHeading,
                                                  globalSubstepLabel,
                                                  nodeHeading
                                                )
                                              );
                                            }}
                                            checked={
                                              selectedNodeInfo &&
                                              selectedNodeInfo.nodeId ===
                                                item.rich_text_node_id
                                            }
                                            style={
                                              isSourceItem
                                                ? {
                                                    accentColor: "#444444", // Dark grey for element being moved
                                                    backgroundColor: "#444444",
                                                  }
                                                : selectedNodeInfo &&
                                                  selectedNodeInfo.nodeId ===
                                                    item.rich_text_node_id
                                                ? {
                                                    accentColor: "#0066cc", // Blue for selected node
                                                    backgroundColor: "#0066cc",
                                                  }
                                                : {}
                                            }
                                            disabled={isSourceItem}
                                          />
                                          {label}
                                        </label>

                                        <div
                                          style={{
                                            marginLeft: "5px",
                                            marginTop: "-10px",
                                            clear: "both",
                                          }}
                                        >
                                          {item.short_description}
                                        </div>
                                        <div
                                          style={{
                                            marginLeft: "4px",
                                            marginTop: "5px",
                                            clear: "both",
                                          }}
                                        >
                                          {item.category_data?.category_name}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            marginLeft: "4px",
                                            marginTop: "5px",
                                            clear: "both",
                                          }}
                                        >
                                          <div
                                            style={{
                                              marginTop: "-6px",
                                              clear: "both",
                                            }}
                                            dangerouslySetInnerHTML={{
                                              __html: item.rich_text_content,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  }
                                  case "video": {
                                    // Get the display video number for this substep
                                    const substepDisplayVideoNumber =
                                      videoCounter++;

                                    // Debug video numbering
                                    if (isSourceStep) {
                                    }

                                    // Determine if this is the source video using the same logic as other items
                                    const isMovingThisVideo = isSourceItem;

                                    // Debug log for video component
                                    if (isSourceStep) {
                                    }

                                    const nodeHeading = `Video ${substepDisplayVideoNumber}`;
                                    // Use global counter for display
                                    const label = (
                                      <HeadingWithTreePath
                                        title={nodeHeading}
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "video",
                                          nodeIndex:
                                            substepDisplayVideoNumber - 1,
                                        }}
                                        color={
                                          isMovingThisVideo
                                            ? undefined
                                            : "#0d6efd"
                                        } // Blue color only for items NOT being moved
                                      />
                                    );

                                    // Now render the component
                                    return (
                                      <div
                                        key={item.video_tree_node_id || label}
                                        style={{ clear: "both" }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            clear: "both",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onChange={() => {
                                              if (
                                                selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.video_tree_node_id
                                              ) {
                                                setSelectedNodeInfo(null);
                                                setSelectionType(null);
                                              } else {
                                                setSelectedNodeInfo({
                                                  nodeId:
                                                    item.video_tree_node_id,
                                                  sectionIndex: index,
                                                  stepIndex: stepIndex,
                                                  nodeIndex: itemIndex,
                                                  globalNodeNumber:
                                                    substepDisplayVideoNumber,
                                                  nodeType: "Video",
                                                  direction: null,
                                                  substepIndex,
                                                  stepId,
                                                });
                                                setTargetSectionData({
                                                  sectionId:
                                                    section.section_tree_node_id,
                                                  siblingOrder:
                                                    section.sibling_order,
                                                  sectionTreeId:
                                                    section.section_tree_node_id,
                                                  stepId,
                                                  stepTreeId:
                                                    step.procedure_tree_ref,
                                                  stepSiblingOrder:
                                                    step.sibling_order ||
                                                    step.procedure_tree
                                                      ?.sibling_order,
                                                  elementSiblingOrder:
                                                    item.sibling_order,
                                                  elementId:
                                                    item.video_tree_node_id,
                                                  elementTreeId:
                                                    item.procedure_tree_ref,
                                                  substepId,
                                                  substepTreeId:
                                                    substep.procedure_tree_ref,
                                                  substepSiblingOrder:
                                                    substep.sibling_order ||
                                                    substep.procedure_tree
                                                      ?.sibling_order,
                                                });
                                                setSelectionType("node");
                                              }
                                              setToMoveCopy(
                                                getMoveCopyInfo(
                                                  "to",
                                                  sectionHeading,
                                                  stepHeading,
                                                  globalSubstepLabel,
                                                  nodeHeading
                                                )
                                              );
                                            }}
                                            checked={
                                              isMovingThisVideo ||
                                              (selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.video_tree_node_id)
                                            }
                                            style={
                                              isMovingThisVideo
                                                ? {
                                                    accentColor: "#444444", // Dark grey for video being moved
                                                    backgroundColor: "#444444",
                                                  }
                                                : {}
                                            }
                                            disabled={isMovingThisVideo}
                                          />
                                          {label}
                                        </label>

                                        <video
                                          src={`${
                                            item.video.includes("http")
                                              ? item.video
                                              : `${BASE}${item.video}`
                                          }`}
                                          controls
                                          style={{
                                            width: "100%",
                                            maxHeight: "360px",
                                            borderRadius: "6px",
                                            backgroundColor: "#000",
                                            clear: "both",
                                          }}
                                        />

                                        <div style={{ clear: "both" }}>
                                          {item.video_url}
                                        </div>

                                        <div style={{ clear: "both" }}>
                                          {item.short_description}
                                        </div>
                                      </div>
                                    );
                                  }
                                  case "canvas": {
                                    const pages = getPages(item);

                                    const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                                    const nodeHeading = `Canvas ${globalCanvasNumber}`;
                                    const label = (
                                      <HeadingWithTreePath
                                        title={nodeHeading}
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "canvas",
                                          nodeIndex: globalCanvasNumber - 1,
                                        }}
                                        color="#0d6efd" // Blue color for all items
                                      />
                                    );

                                    return (
                                      <div
                                        key={item.canvas_tree_node_id || label}
                                        style={{
                                          marginTop: "40px",
                                          clear: "both",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            clear: "both",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onChange={() => {
                                              if (
                                                selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.canvas_tree_node_id
                                              ) {
                                                setSelectedNodeInfo(null);
                                                setSelectionType(null);
                                              } else {
                                                setSelectedNodeInfo({
                                                  nodeId:
                                                    item.canvas_tree_node_id,
                                                  sectionIndex: index,
                                                  stepIndex: stepIndex,
                                                  nodeIndex: itemIndex, // Use the actual array index for positioning
                                                  globalNodeNumber:
                                                    globalCanvasNumber,
                                                  nodeType: "Canvas",
                                                  direction: null,
                                                  substepIndex,
                                                  stepId,
                                                });
                                                setTargetSectionData({
                                                  sectionId:
                                                    section.section_tree_node_id,
                                                  siblingOrder:
                                                    section.sibling_order,
                                                  sectionTreeId:
                                                    section.section_tree_node_id,
                                                  stepId,
                                                  stepTreeId:
                                                    step.procedure_tree_ref,
                                                  stepSiblingOrder:
                                                    step.sibling_order ||
                                                    step.procedure_tree
                                                      ?.sibling_order,
                                                  elementSiblingOrder:
                                                    item.sibling_order,
                                                  elementId:
                                                    item.canvas_tree_node_id,
                                                  elementTreeId:
                                                    item.procedure_tree_ref,
                                                  substepId,
                                                  substepTreeId:
                                                    substep.procedure_tree_ref,
                                                  substepSiblingOrder:
                                                    substep.sibling_order ||
                                                    substep.procedure_tree
                                                      ?.sibling_order,
                                                });
                                                setSelectionType("node");
                                              }
                                              setToMoveCopy(
                                                getMoveCopyInfo(
                                                  "to",
                                                  sectionHeading,
                                                  stepHeading,
                                                  globalSubstepLabel,
                                                  nodeHeading
                                                )
                                              );
                                            }}
                                            checked={
                                              selectedNodeInfo &&
                                              selectedNodeInfo.nodeId ===
                                                item.canvas_tree_node_id
                                            }
                                            style={
                                              isSourceItem
                                                ? {
                                                    accentColor: "#444444", // Dark grey for element being moved
                                                    backgroundColor: "#444444",
                                                  }
                                                : {}
                                            }
                                            disabled={isSourceItem}
                                          />
                                          {label}
                                        </label>

                                        {/* Display all pages vertically */}
                                        <CanvasDisplay pages={pages} />
                                      </div>
                                    );
                                  }
                                  case "attachment": {
                                    const attachmentList =
                                      item.attachments || [];
                                    if (attachmentList.length === 0)
                                      return null;

                                    const globalAttachmentNumber =
                                      attachmentCounter++;
                                    const nodeHeading = `Attachment ${globalAttachmentNumber}`;

                                    // Debug log attachment number in source step
                                    if (isSourceStep) {
                                    }

                                    const label = (
                                      <HeadingWithTreePath
                                        title={nodeHeading}
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "attachment",
                                          nodeIndex: globalAttachmentNumber - 1,
                                        }}
                                        color="#0d6efd" // Blue color for all items
                                      />
                                    );

                                    // Determine if this is the source attachment using a direct comparison
                                    // This is a more straightforward way to identify the attachment being moved

                                    return (
                                      <div
                                        key={
                                          item.attachment_tree_node_id ||
                                          `attachment-${itemIndex}`
                                        }
                                        style={{ clear: "both" }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            marginTop: "20px",
                                            clear: "both",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onChange={() => {
                                              if (
                                                selectedNodeInfo &&
                                                selectedNodeInfo.nodeId ===
                                                  item.attachment_tree_node_id
                                              ) {
                                                setSelectedNodeInfo(null);
                                                setSelectionType(null);
                                              } else {
                                                setSelectedNodeInfo({
                                                  nodeId:
                                                    item.attachment_tree_node_id,
                                                  sectionIndex: index,
                                                  stepIndex: stepIndex,
                                                  nodeIndex: itemIndex, // Use the actual array index for positioning
                                                  globalNodeNumber:
                                                    globalAttachmentNumber,
                                                  nodeType: "Attachment",
                                                  direction: null,
                                                  substepIndex,
                                                  stepId,
                                                });
                                                setTargetSectionData({
                                                  sectionId:
                                                    section.section_tree_node_id,
                                                  siblingOrder:
                                                    section.sibling_order,
                                                  sectionTreeId:
                                                    section.section_tree_node_id,
                                                  stepId,
                                                  stepTreeId:
                                                    step.procedure_tree_ref,
                                                  stepSiblingOrder:
                                                    step.sibling_order ||
                                                    step.procedure_tree
                                                      ?.sibling_order,
                                                  substepId,
                                                  substepTreeId:
                                                    substep.procedure_tree_ref,
                                                  substepSiblingOrder:
                                                    substep.sibling_order ||
                                                    substep.procedure_tree
                                                      ?.sibling_order,
                                                  elementSiblingOrder:
                                                    item.sibling_order,
                                                  elementId:
                                                    item.attachment_tree_node_id,
                                                  elementTreeId:
                                                    item.procedure_tree_ref,
                                                });
                                                setSelectionType("node");
                                              }
                                              setToMoveCopy(
                                                getMoveCopyInfo(
                                                  "to",
                                                  sectionHeading,
                                                  stepHeading,
                                                  globalSubstepLabel,
                                                  nodeHeading
                                                )
                                              );
                                            }}
                                            checked={
                                              selectedNodeInfo &&
                                              selectedNodeInfo.nodeId ===
                                                item.attachment_tree_node_id
                                            }
                                            style={
                                              isSourceItem
                                                ? {
                                                    accentColor: "#444444", // Dark grey for element being moved
                                                    backgroundColor: "#444444",
                                                  }
                                                : {}
                                            }
                                            disabled={isSourceItem}
                                          />
                                          {label}
                                        </label>
                                        <div>{item.short_description}</div>
                                        {attachmentList.map((attachment, i) => (
                                          <div
                                            key={i}
                                            style={{
                                              marginLeft: "20px",
                                              clear: "both",
                                            }}
                                          >
                                            {renderPreview(attachment)}
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }
                                  default:
                                    return null;
                                }
                              })}
                            </div>
                          );
                        }
                      )}
                    </div>
                  );
                }
              )}
            </div>
          );
        })}

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
      <div style={{ height: "100px" }}></div>
    </div>
  );
};
export default ProcedureVideoMove;
